import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  Menu,
  List,
  ListItem,
  ListSeparator,
  TreeDotsIcon,
  IconButton,
  EditIcon,
  CheckboxCircleArrowIcon,
  TrashIcon,
} from "@activecollab/components";
import classnames from "classnames";
import PropTypes from "prop-types";
import { moveToTrash, convertToTask } from "~/store/actions/actions.subtasks.jsx";
import { getRawConfigOption } from "~/store/selectors/selector.configOptions.jsx";
import { hasCollaborativePermissionsOnProject } from "~/store/selectors/selector.users";
import ConfirmLink from "../component.confirm_link.jsx";

const SubtaskPanel = ({ id, projectId, taskId, onEditSubtask, convertToTaskReact }) => {
  const { t } = useTranslation();
  const canCollaborateOnProject = useSelector((state) => hasCollaborativePermissionsOnProject(state, projectId));
  const dispatch = useDispatch();

  const [openMenu, setOpenMenu] = useState(false);

  const handleEditSubtask = useCallback(() => {
    onEditSubtask(id);
  }, [id, onEditSubtask]);

  const handleMoveToTrash = useCallback(() => {
    dispatch(moveToTrash(id));
  }, [dispatch, id]);

  const handleConvertToTask = useCallback(() => {
    dispatch(convertToTask(projectId, taskId, id)).then((res) => {
      const { response = { entities: {} } } = res;
      const { entities = { tasks: {} } } = response;
      const { result = { single: {} } } = response;
      const { tasks = {} } = entities;
      const { single = {} } = result;
      if (tasks && single) {
        convertToTaskReact(tasks[single]);
      }
    });
  }, [convertToTaskReact, dispatch, id, projectId, taskId]);

  const handleOpen = useCallback(() => {
    setOpenMenu(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpenMenu(false);
  }, []);

  const isTaskDependenciesEnabled = useSelector((state) => getRawConfigOption(state, "task_dependencies_enabled"));

  return (
    <Menu
      target={
        <IconButton
          className={classnames({ open: openMenu }, "c-simple-menu__trigger tw-h-8 tw-w-8")}
          variant="text gray"
          active={openMenu}
        >
          <TreeDotsIcon />
        </IconButton>
      }
      onOpen={handleOpen}
      onClose={handleClose}
    >
      <List className="tw-py-2">
        <ListItem onClick={handleEditSubtask}>
          <EditIcon className="tw-fill-theme-900" />
          {t("Edit")}
        </ListItem>
        {canCollaborateOnProject ? (
          <ConfirmLink
            href="#"
            title={t("Create Task from Subtask")}
            description={
              isTaskDependenciesEnabled
                ? t(
                    "The new task will retain the assignee and date. It will also become a predecessor of the current task."
                  )
                : t("The new task will retain the assignee.")
            }
            confirmLabel={t("Create Task")}
            onClick={handleConvertToTask}
            className="tw-no-underline"
          >
            <ListItem>
              <CheckboxCircleArrowIcon className="tw-fill-theme-900" />
              {t("Create Task")}
            </ListItem>
          </ConfirmLink>
        ) : null}
        <ListSeparator />
        <ConfirmLink
          href="#"
          title={t("Delete subtask?")}
          description={t("The subtask will remain in Trash for 30 days.")}
          confirmLabel={t("Delete")}
          onClick={handleMoveToTrash}
          className="tw-no-underline"
        >
          <ListItem>
            <TrashIcon className="tw-fill-theme-900" />
            {t("Delete")}
          </ListItem>
        </ConfirmLink>
      </List>
    </Menu>
  );
};

SubtaskPanel.propTypes = {
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  projectId: PropTypes.number,
  taskId: PropTypes.number,
  onEditSubtask: PropTypes.func,
  convertToTaskReact: PropTypes.func,
};

SubtaskPanel.defaultProps = {};

export default SubtaskPanel;
